// product-cols.jsx — Pure column-definition constants extracted from ProductsView.jsx.
// Behavior-preserving extraction only; no logic changes.
// Stateful / JSX render logic stays in ProductsView.jsx.
//
// Exports:
//   PARAM_COLS                         — configuration parameter grid columns
//   ProductStatusCell                  — icon+word status badge (color-blind safe)
//   makeProductCols(flatFamilies)      — single-row product-info grid columns
//   makeGridCols(mode, onNavToCatalog) — component cost grid columns

import MoneyCell from './MoneyCell.jsx';
import HourCell from './HourCell.jsx';
import Text from './components/Text.jsx';
import { BTN_GLYPH } from './theme-tokens.js';
import { formatQty } from './formatMoney.js';
import { rate, qtyDetail } from './format.js';
import { OnshapeLink } from './OnshapeLink.jsx';
import LevelCascade from './components/LevelCascade.jsx';
import { CATALOG, PRODUCT, FORMULA, WARN, OnshapeMark } from './icons.js';

// isFormulaQty — CO11: true when qty_expr is a real formula (present and NOT a bare
// numeric constant like "2" or "3.5"). A leading '=' or any non-numeric char → formula.
// Display-only helper for the FORMULA badge; does not evaluate.
function isFormulaQty(qty_expr) {
  if (qty_expr == null) return false;
  const s = String(qty_expr).trim();
  if (!s) return false;
  const bare = s.replace(/^=\s*/, '').trim();       // "=2" is still a bare constant
  return !/^-?\d+(\.\d+)?$/.test(bare);             // not a plain number → formula
}

// ---------------------------------------------------------------------------
// KIND_META — Source vocabulary: icon + word (color-blind safe: meaning by icon
// shape + word, never color). S3g: consumed by the merged Source·Ref column render.
// ---------------------------------------------------------------------------
const KIND_META = {
  line:    { icon: CATALOG, label: 'Catalog', color: 'var(--th-navy)' },
  product: { icon: PRODUCT, label: 'Product', color: 'var(--th-product-accent)' },
};

// N/A dash — read-mode display for a boolean/list param's Unit/Min/Max cells (S1a/S1b:
// those fields don't apply to a Y/N or List param). Matches the "NA" convention used for
// Size (see sizeDisplay in product-resolver.js / the Size column render below).
const NA_CELL = <Text kind="empty">NA</Text>;

// SizeRange — narrow a size-list option set to a param's numeric [min,max] window
// (min ≤ nps ≤ max). Applied only to size-unit lists (options carry `nps`); a
// param with neither min nor max set is returned unchanged (all options). Custom
// lists have no nps, so they are never filtered.
function filterByMinMax(opts, min, max) {
  const lo = (min != null && min !== '' && Number.isFinite(Number(min))) ? Number(min) : null;
  const hi = (max != null && max !== '' && Number.isFinite(Number(max))) ? Number(max) : null;
  if (lo == null && hi == null) return opts;
  return opts.filter(o => {
    if (o.num == null) return true;             // option with no numeric axis → don't drop it
    const n = Number(o.num);
    return (lo == null || n >= lo) && (hi == null || n <= hi);
  });
}

// S1b — resolve a `list` param's option set: 'custom' reads the param's own `options`
// JSON column; a standard unit (a size_lists.list_name, e.g. NPS) looks it up in the
// caller-supplied cache (ProductsView fetches/caches size_lists rows per list_name).
// Display ≠ value throughout: label=look shown, value=size_id (or custom value) stored.
// SizeRange: a size-unit list with a numeric min/max is narrowed to that window.
export function resolveListOptions(row, sizeListValuesByName) {
  if (!row || row.type !== 'list') return [];
  if (row.size_unit === 'custom') {
    try {
      const arr = JSON.parse(row.options || '[]');
      return Array.isArray(arr) ? arr : [];
    } catch { return []; }
  }
  const opts = row.size_unit ? (sizeListValuesByName[row.size_unit] || []) : [];
  return filterByMinMax(opts, row.min, row.max);
}

// S3 — true for a param whose Min/Max are the durable, editable valid-nps-range (a
// size-unit list: kind='list' with a real Size Unit, not 'custom'). Custom lists have
// no nps to range over, boolean/count/magnitude don't use this path at all.
function isSizeUnitList(row) {
  return !!row && row.type === 'list' && !!row.size_unit && row.size_unit !== 'custom';
}

// S3 — Min/Max dropdown options for a size-unit list param: {value,label} keyed by the
// NUMERIC nps (not size_id, unlike the Typical column) since min/max store the nps
// directly. Sourced from the same sizeListValuesByName cache as Typical's options.
function sizeUnitMinMaxOptions(row, sizeListValuesByName) {
  if (!isSizeUnitList(row)) return [];
  const opts = sizeListValuesByName[row.size_unit] || [];
  return opts.filter(o => o.num != null).map(o => ({ value: o.num, label: o.label }));
}

// S3 — read-mode Min/Max look for a size-unit list param: the size_lists "look" (e.g.
// 2 1/2") of the size_lists row whose nps == the stored min/max. Blank/— if unset; falls
// back to the raw stored number if no size_lists row still matches it (defensive — e.g.
// a size list was re-seeded and dropped that nps).
function sizeUnitMinMaxLook(value, row, sizeListValuesByName) {
  if (value == null || value === '') return <Text kind="empty" />;
  const opts = sizeUnitMinMaxOptions(row, sizeListValuesByName);
  const match = opts.find(o => Number(o.value) === Number(value));
  return <span>{match ? match.label : value}</span>;
}

// ---------------------------------------------------------------------------
// makeParamCols — configuration parameter grid columns (inline-editable).
// unitOptions: string[] — the DISTINCT in-use units (catalog_lines.unit + unit enum),
//   used for the Unit dropdown (WS1a-ii C5). An off-list existing value is preserved by
//   SelectCell, so no data is dropped even if a param's unit isn't in the list.
// sizeUnitOptions: [{value,label}] — S1b: the Size Unit choices (size_lists.list_name
//   values + 'custom'), used for the Size Unit dropdown on kind='list' rows.
// sizeListValuesByName: { [list_name]: [{value,label}] } — S1b: cached size_lists rows
//   per list_name (value=size_id, label=look), used to resolve a standard-unit list
//   param's Typical dropdown. ProductsView owns the fetch/cache; this stays pure.
// S1a: a param with kind='boolean' (Y/N) reads clearly by word, never color alone —
// Typical shows Yes/No, Unit/Min/Max show NA. The server (product-db.js) is the single
// source of truth for the NA invariant (it force-clears unit/min/max for kind='boolean'
// on every create/update), so these read-mode renders always agree with the DB.
// S1b: a param with kind='list' is the same story — Typical resolves to a dropdown of
// the Size Unit's options (display=look, value=size_id/custom value); Unit/Min/Max show
// NA (the server force-clears them for kind='list' too).
// ---------------------------------------------------------------------------
// A param is INTERNAL when Config? is unticked. Nullable column: an older row with
// no value is a knob, matching how everything behaved before the flag existed.
const isInternal = row => row && row.configurable != null && !Number(row.configurable);

export function makeParamCols(unitOptions = [], sizeUnitOptions = [], sizeListValuesByName = {}) {
  return [
    { key: 'name',          label: 'Name',    width: 130, editable: true },
    { key: 'description',   label: 'Description', width: 180, editable: true },
    {
      // CONFIGURABLE vs INTERNAL (Alex, 2026-08-03: "the weight, scrap, and rate
      // must not be configurable parameters but internal!"). Ticked = a knob:
      // broadcast to whoever places or embeds this product, overridable, and
      // simulatable. Unticked = plumbing: never offered anywhere, and free to
      // carry a formula in Typical instead of a fixed value.
      // Word + checkbox, never the checkbox alone — Alex is colour-blind and a
      // bare tick in a narrow column is the kind of thing that reads as noise.
      key: 'configurable', label: 'Config?', width: 74, align: 'center', textKind: 'small',
      editType: 'checkbox',
      editValue: row => (row.configurable == null ? 1 : Number(row.configurable)) ? '1' : '0',
      render: row => (row.configurable == null ? 1 : Number(row.configurable))
        ? <Text>Yes</Text>
        : <Text kind="note" title="Internal — not offered when this product is placed or embedded">internal</Text>,
    },
    {
      key: 'type', label: 'Type', width: 90, editType: 'select',
      options: [
        { value: 'count',     label: 'count' },
        { value: 'magnitude', label: 'magnitude' },
        { value: 'boolean',   label: 'Y/N' },
        { value: 'list',      label: 'List' },
      ],
    },
    {
      // S1b: Size Unit — only meaningful for kind='list'. Drives the Typical dropdown's
      // option set below (standard unit → size_lists rows for that list_name; 'custom' →
      // the param's own inline options).
      key: 'size_unit', label: 'Size Unit', width: 90,
      editType: row => row.type === 'list' ? 'select' : undefined,
      options: sizeUnitOptions,
      render: row => row.type === 'list' ? (row.size_unit || '—') : NA_CELL,
    },
    {
      // S1a/S1b: row-aware editType (DataGrid v35+) — a boolean param inline-edits its
      // Typical as a checkbox (reuses CheckboxCell, checked=Yes, stores 1/0); a list
      // param inline-edits its Typical as a dropdown of resolved options (row-aware
      // `options`, DataGrid v36 — display=look/label, value=size_id/custom value); every
      // other kind keeps the existing number/text edit (editable:true, no editType).
      key: 'typical_value', label: 'Typical', width: 112, align: 'right', editable: true,
      // An INTERNAL param may compute itself instead of holding a value (Alex:
      // "sometime, it is cool to have a parameter I can use for intermediate
      // calculation. Can they be changed to accept formulas? Of course, it will
      // only for the one that are not configurable / simulate."). So the SAME
      // Typical cell becomes a formula box once Config? is unticked — one column,
      // row-aware, exactly like it already switches to a checkbox for Y/N and a
      // dropdown for List. The staged value is translated to value_expr in
      // handleParamCellSave, exactly as a List row's is translated to typical_text —
      // one column key, the handler decides which field it lands in.
      editType: row => isInternal(row) ? 'formula'
        : row.type === 'boolean' ? 'checkbox' : row.type === 'list' ? 'select' : undefined,
      options: row => resolveListOptions(row, sizeListValuesByName),
      // S2.2: the select's options are keyed by typical_text (size_id/custom value),
      // not typical_value (the numeric nps) — seed/read the edit buffer from typical_text
      // for 'list' rows so the closed select matches an option (shows the look, not the
      // raw nps). typical_value stays the buffer field for every other kind, unchanged.
      editValue: row => isInternal(row) ? (row.value_expr ?? '')
        : row.type === 'list' ? row.typical_text : row.typical_value,
      render: row => {
        // A derived param shows its FORMULA with the badge the app already uses
        // for a computed qty, and the value it resolves to underneath — the formula
        // is what you edit, the number is what you need to see.
        if (isInternal(row) && row.value_expr) {
          // BADGE PINNED LEFT, formula right-aligned in what is left (Alex, 2026-08-03:
          // "can the f be always on the left of the cell? Similar to what we did for
          // $"). Same construction as MoneyCell's $ and the Qty column's own badge: a
          // fixed-width flex-shrink:0 box for the badge, the value filling the rest.
          // Centring it on the text made every formula row's badge sit at a different
          // x, which is what he is reacting to.
          return (
            <span style={{ display: 'flex', width: '100%', alignItems: 'baseline' }}>
              <span style={{ flexShrink: 0, width: 12, color: 'var(--th-accent)', fontStyle: 'italic' }}>{FORMULA}</span>
              <span style={{ flex: 1, textAlign: 'right', fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={row.value_expr}>{row.value_expr}</span>
            </span>
          );
        }
        if (row.type === 'boolean') return <span>{Number(row.typical_value) ? 'Yes' : 'No'}</span>;
        if (row.type === 'list') {
          const opts = resolveListOptions(row, sizeListValuesByName);
          const match = opts.find(o => String(o.value) === String(row.typical_text));
          return <span>{match ? match.label : (row.typical_text || '—')}</span>;
        }
        return row.typical_value ?? '';
      },
    },
    {
      key: 'unit', label: 'Unit', width: 80, editType: 'select', options: unitOptions,
      render: row => (row.type === 'boolean' || row.type === 'list') ? NA_CELL : (row.unit ?? ''),
    },
    {
      // S3: a size-unit list param's Min is durable (server keeps it, see product-db.js)
      // and editable here as an NPS dropdown (row-aware editType/options, mirroring the
      // Typical column) — renders the size_lists "look", not NA. Custom list / boolean
      // stay NA (unchanged); count/magnitude keep the plain numeric edit (unchanged).
      key: 'min', label: 'Min', width: 60, align: 'right', editable: true,
      editType: row => isSizeUnitList(row) ? 'select' : undefined,
      options: row => sizeUnitMinMaxOptions(row, sizeListValuesByName),
      render: row => {
        if (row.type === 'boolean' || (row.type === 'list' && row.size_unit === 'custom')) return NA_CELL;
        if (isSizeUnitList(row)) return sizeUnitMinMaxLook(row.min, row, sizeListValuesByName);
        return row.min ?? '';
      },
    },
    {
      // S3: same as Min, for Max.
      key: 'max', label: 'Max', width: 60, align: 'right', editable: true,
      editType: row => isSizeUnitList(row) ? 'select' : undefined,
      options: row => sizeUnitMinMaxOptions(row, sizeListValuesByName),
      render: row => {
        if (row.type === 'boolean' || (row.type === 'list' && row.size_unit === 'custom')) return NA_CELL;
        if (isSizeUnitList(row)) return sizeUnitMinMaxLook(row.max, row, sizeListValuesByName);
        return row.max ?? '';
      },
    },
  ];
}

// PARAM_COLS — backward-compat: the column set with a free-text unit (no dropdown options).
// Retained so any other consumer keeps compiling; the Config grid now uses makeParamCols.
export const PARAM_COLS = makeParamCols();

// Product status vocabulary — icon + word so it aligns with the StatusGlyph system.
// Color is supplemental only; the icon + word carry meaning (color-blind safe).
const PROD_STATUS_META = {
  active:     { icon: '✓', word: 'active',     color: 'var(--th-status-synced)' },
  draft:      { icon: '○', word: 'draft',       color: 'var(--th-status-draft)' },
  deprecated: { icon: '✗', word: 'deprecated',  color: 'var(--th-status-deprecated)' },
};

// StatusWord — icon + word badge for product/placement status rows (color-blind safe).
// Mirrors StatusCell layout but uses product vocabulary (active/draft/deprecated).
export function ProductStatusCell({ value }) {
  const m = PROD_STATUS_META[value] || { icon: '?', word: value || 'draft', color: 'var(--th-status-draft)' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-small)', color: m.color, whiteSpace: 'nowrap' }}>
      <span aria-hidden="true" style={{ fontFamily: 'var(--ff-mono)', fontWeight: 700 }}>{m.icon}</span>
      <span>{m.word}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// OnshapeRefCell — display for the Onshape column (P1a): the ↗ OnshapeLink icon
// when set, plus a muted shortened ref (first 6 chars of did + last segment,
// e.g. "abc123…/e4f5") so the row is scannable without showing the full path.
// Edit mode (TextCell, via editable:true) shows/accepts the full ref verbatim.
// ---------------------------------------------------------------------------
function shortenOnshapeRef(ref) {
  const parts = String(ref).trim().split('/');
  const last = parts[parts.length - 1] || '';
  return `${parts[0].slice(0, 6)}…/${last.slice(-4)}`;
}

export function OnshapeRefCell({ value }) {
  if (!value) return <Text kind="empty" />;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <OnshapeLink ref={value} />
      <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)', color: 'var(--th-text-secondary)' }}>
        {shortenOnshapeRef(value)}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// makeProductCols — single-row inline-editable DataGrid for the product header
// ---------------------------------------------------------------------------
// P4: the legacy "Family" column is dropped — "family" was just a name for an L2/L3
// category; the L1→L2→L3 path lives in the left tree. Product identity = Tag + Label.
// tradeOptions: [{value, label}] incl. leading '— none —' — fetched from
// /api/options/trades (one source of truth, Q5); ProductsView supplies it.
// PRODUCT-ONLY extras for the product header row (UI_VISION §5, merged
// 2026-07-20). Tag/Description/Status are NOT defined here anymore — they were
// the fork ("Today Tag/Label/Status are defined twice for one product — a fork
// by definition"): the header row now takes them from the SHARED folder grid
// (folder-cols.jsx makeFolderCols, statusEditable) and appends these columns.
export function makeProductCols(laborRows = []) {
  const laborPickable = laborRows.filter(l => l.status !== 'deprecated');
  return [
    {
      // Labour is now a catalog-line pick, not a trade (Alex, 2026-07-19: "I
      // thought I have to pick a labor from HR not a trade"). Options are the
      // is_labor=1 HR lines; the LINE carries the rate. `products.trade` still
      // exists but is dormant and no longer offered here — updateProduct keeps
      // it in step for any non-UI writer.
      //
      // `_labor_source` tells us whether this was chosen on the product or
      // INHERITED from its category; an inherited rate is shown as inherited
      // rather than applied silently, because a wrong default is worse than a
      // missing one (zero is at least visibly absent).
      // THE cascade, not a flat list (Alex, 2026-08-03: "must be similar to the
      // other labor pick: Auto cascade drop down, filtered by is labor"). The flat
      // select showed "Electrician $58.00/hr" SIX times -- once per HR shelf --
      // because the shelf is the only thing telling them apart and a flat list
      // cannot show it. Same LevelCascade, same rows, as the catalog's FabLabour
      // pick: L2 (labour group) -> L3 (discipline) -> trade, auto-advancing,
      // staged through ctx.buffer so Cancel undoes it.
      //
      // "filtered by is labor" needs no filter here: getLaborLineOptions already
      // pins c1.code='HR' AND is_labor=1, so every row in the cascade IS labour
      // and only shelves that actually hold labour lines can appear.
      key: 'labor_line', label: 'Labour', width: 215,
      render: (row, ctx) => {
        if (ctx?.inEdit) {
          const cur = ctx.buffered && 'labor_line' in ctx.buffered ? ctx.buffered.labor_line : row.labor_line;
          return (
            <LevelCascade
              rows={laborPickable}
              value={cur}
              labels={{ l2: 'Labour group', l3: 'Labour discipline', leaf: 'Labour trade' }}
              onPick={id => ctx?.buffer?.(row.id, 'labor_line', id || '')}
            />
          );
        }
        const rateText = row._labor_rate != null ? rate(row._labor_rate, 'hr') : null;
        if (!row._labor_label) {
          // Only a WARNING when there are hours to charge (_uncharged_hours, summed by
          // ProductsView from the live grid): a product with no install hours needs no
          // labour line, and saying "hours not charged" there was noise that taught
          // you to stop reading it.
          const warn = row._uncharged_hours > 0;
          return warn
            ? <Text kind="deprecated">{WARN} none: {formatQty(row._uncharged_hours)} h not charged</Text>
            : <span style={{ color: 'var(--th-text-muted)', fontSize: 'var(--fs-small)' }}>— none</span>;
        }
        return (
          <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.2 }}>
            <span style={{ color: 'var(--th-text-body)' }}>{row._labor_label} {rateText}</span>
            {row._labor_source === 'category' && (
              <span style={{ color: 'var(--th-text-muted)', fontSize: 'var(--fs-micro)' }}>from category</span>
            )}
          </span>
        );
      },
    },
    {
      // QUANTITY-BASED vs CONFIGURABLE-FIXED (Alex, 2026-08-03: "Is the solution,
      // when we do a product, to have configurable fixed or quantity-based?").
      // SET   → Qty IS the size, in the named parameter's unit. A recipe written
      //         at a readable 1000 kg is priced ONCE at 1000 kg, not multiplied,
      //         so the typical stops doing arithmetic.
      // EMPTY → configurable-fixed: the knobs describe the thing, Qty counts how
      //         many of them.
      //
      // Until now this could only be set by script, which made every conversion a
      // "run this for me" round trip. ⚠ THE VALIDATION STAYS ON THE SERVER
      // (assertQtyParamShape): the driver must be a NUMBER, must be CONFIGURABLE
      // (an override never reaches an internal param, so an internal driver could
      // never be set and would price at its typical forever), and must be the ONLY
      // knob. This control offers the plausible drivers and lets the server's
      // sentence come back into the Notice — it deliberately does not re-implement
      // the rules, because two copies of a rule is how they drift.
      //
      // ⚠ WHAT THIS CONTROL CANNOT DO, and it matters: the flag and the COMPONENT
      // conversion are one operation. A component that carries the size in its
      // param_map with qty=1 collapses to one unit the moment the driver wins over
      // the map (measured: flagging 598 alone took two products from $105,477.52 to
      // $10.22). Flipping this on an existing product with such components needs
      // scripts/enable-quantity-based.mjs, which moves the map into qty_expr in the
      // same transaction. Safe here for a NEW product, or one whose comps already
      // carry the size in Qty.
      key: 'qty_param', label: 'Quantity', width: 190,
      editType: 'select',
      editValue: row => row.qty_param ?? '',
      options: row => [
        { value: '', label: 'Configurable — Qty counts' },
        // Only the params that could legally BE the driver. A boolean or list
        // parameter is not a magnitude, and an internal one can never be set.
        ...(row.params || [])
          .filter(p => p.type !== 'boolean' && p.type !== 'list')
          .filter(p => p.configurable == null || Number(p.configurable))
          .map(p => ({ value: p.name, label: `Qty is ${p.name}${p.unit ? ` (${p.unit})` : ''}` })),
      ],
      render: row => {
        if (!row.qty_param) {
          return <Text kind="note"
                       title="Configurable — the parameters describe it and Qty counts how many">configurable</Text>;
        }
        const driver = (row.params || []).find(p => p.name === row.qty_param);
        return (
          <span style={{ color: 'var(--th-text-body)' }}
                title={`Quantity-based — Qty is this product's ${row.qty_param}, not a count`}>
            Qty is {row.qty_param}{driver?.unit ? ` (${driver.unit})` : ''}
          </span>
        );
      },
    },
    {
      key: 'placeable', label: 'Placeable', width: 85,
      editType: 'select',
      options: [{ value: '1', label: 'Yes' }, { value: '0', label: 'No' }],
      render: row => <span style={{ color: row.placeable ? 'var(--th-status-synced)' : 'var(--th-text-disabled)' }}>{row.placeable ? 'Yes' : 'No'}</span>,
    },
    {
      // The one string a CUSTOMER reads about this product (Alex, 2026-08-12: "Best
      // would be to have a small description that goes with the product"). The
      // Proposal screen prints it verbatim in place of the catalog lines, so it is
      // edited here or it is not edited at all. seed-product-descriptions.mjs wrote
      // a first draft from each product's contents — those are drafts, not answers.
      // Labelled "Customer Description", not "Description": the product-name column in
      // this same row is ALREADY labelled Description, and two columns with one name is
      // how the wrong one gets edited.
      key: 'description', label: 'Customer Description', width: 260, editable: true,
      render: row => (
        <span style={{ color: row.description ? 'var(--th-text-body)' : 'var(--th-text-disabled)' }}
              title={row.description || ''}>
          {row.description || '—'}
        </span>
      ),
    },
    {
      // Phase 1a — editable Onshape link (did/wid/eid[/pid]); empty clears the link.
      // Edit mode: plain text input (TextCell, via editable:true). Display: ↗ icon +
      // muted shortened ref. Server validates on save (product-db.js updateProduct).
      key: 'onshape_ref', label: 'Onshape', width: 150, editable: true,
      render: row => <OnshapeRefCell value={row.onshape_ref} />,
    },
  ];
}

// ---------------------------------------------------------------------------
// Q8 v2 — parse a comp's param_map column (JSON string) → plain object, tolerant
// of null/invalid. Shared by the Component column's mapping chip + detail line.
// ---------------------------------------------------------------------------
function parseParamMapCol(raw) {
  if (!raw) return {};
  try { const p = JSON.parse(raw); return (p && typeof p === 'object') ? p : {}; } catch { return {}; }
}

// ---------------------------------------------------------------------------
// MappingFeedback — Mockup B (amended): inline config summary on a param_map
// comp's Component-column cell. Chip = '⇐ N linked · ⚑ M typical'; when
// shows the linked pairs ('Width=0.75 · Height=1.2 · Length=34') and a
// '⚠ <Name> not linked' chip per unlinked sub-param whose name matches a parent
// param (case-insensitive) — the Q8 v2 nudge. No mat/labor/subtotal here; those
// stay the normal grid columns.
// ---------------------------------------------------------------------------
// DASH_CELL — "no config" marker (S4): a catalog-line comp, or a kind='product'
// comp with no ref picked yet. A bare dash reads fine colorblind-safe here —
// nothing in this cell is conveyed by color.
const DASH_CELL = <Text kind="empty" />;

function MappingFeedback({ row }) {
  if (!row.ref_id) return DASH_CELL;

  const paramMap = parseParamMapCol(row.param_map);
  const linkedNames = Object.keys(paramMap);
  // row._sub_param_names (S4, ProductsView): null/undefined = not resolved yet
  // client-side (the server only embeds it when the comp already has a
  // param_map — product-db.js — the grid backfills the rest via its own fetch,
  // same GET the edit form uses); an array (incl. empty) = resolved. Don't guess
  // M (typical count) or run the ⚠ check without it.
  const subParamsKnown = Array.isArray(row._sub_param_names);
  const subParams = subParamsKnown ? row._sub_param_names : [];
  const typicalCount = subParamsKnown ? Math.max(subParams.length - linkedNames.length, 0) : null;

  // ⚠ nudge — an UNLINKED sub param whose name matches a PARENT param (row._parent_param_names,
  // embedded by ProductsView from the product's own params) case-insensitively.
  const parentNamesLower = new Set((row._parent_param_names || []).map(n => n.toLowerCase()));
  const unlinkedMatches = subParamsKnown
    ? subParams.filter(sp => !linkedNames.includes(sp.name) && parentNamesLower.has(sp.name.toLowerCase())).map(sp => sp.name)
    : [];

  const mappedAt = row._mapped_at || {};
  // ⚠ qtyDetail, NOT formatQty — a mapped value is a PARAMETER, not a quantity.
  // formatQty rounds to 0 decimals (right for "5,470 kg"), so a 0.75 m duct
  // width rendered as "Width=1" and a 1.2 m height as "Height=1": the line meant
  // to explain the row was quietly making two different ducts look identical
  // again. Found by reading the rendered page, 2026-08-21.
  const pairs = linkedNames
    .filter(n => mappedAt[n] != null)
    .map(n => `${n}=${qtyDetail(mappedAt[n])}`);

  // ⚠ THE VALUES, NOT THE COUNTS (Alex, 2026-08-21). Two rows of his duct product
  // read identically — same label, same NA size, same qty 1 — at $13,741 and
  // $11,063, and the only thing on screen was "⇐ 3 linked · ⚑ 3 typical" versus
  // "⇐ 2 linked · ⚑ 4 typical". His verdict on that chip: "a little bit
  // meaningless, am I correct?" — correct: a COUNT of mappings can never explain
  // a price, and here the mapping IS the row's identity (Width .75 × Height 1.2
  // is a rectangular duct; Diameter 1 is a round one, and the sub's own Area
  // formula is what makes them differ by 24%).
  //
  // So the pairs render ALWAYS, and the count chip is gone. The line is kept
  // (same two-line row height Alex asked to preserve) and only its content
  // changed — from bookkeeping to the thing that distinguishes the rows.
  const detail = pairs.length ? pairs.join(' · ')
    : subParamsKnown && linkedNames.length === 0 ? 'all params at typical'
    : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
      {detail && (
        <div style={{ fontSize: 'var(--fs-micro)', color: 'var(--th-text-secondary)', overflowWrap: 'break-word' }}
             title={subParamsKnown ? `${linkedNames.length} linked · ${typicalCount} at the sub-product's typical` : undefined}>
          {detail}
        </div>
      )}
      {unlinkedMatches.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {unlinkedMatches.map(name => (
            <span key={name} style={{ fontSize: 'var(--fs-micro)', color: 'var(--th-warn-accent)' }} title={`${name} is not linked but matches a parent param name`}>
              ⚠ {name} not linked
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// makeGridCols — component cost grid columns (A59).
// mode: 'definition' | 'placement' (affects Qty column label)
// onNavigateToCatalog: (l1, l2, l3) => void | null
// onNavigateToProduct: (productId) => void | null   (CO1)
// (The `showMappings` header toggle that used to gate the linked-pairs line is
//   GONE, 2026-08-21: the pairs ARE the row's identity, so hiding them behind a
//   toggle was hiding the answer. A control whose only job was to reveal the
//   useful half of a cell is a control that should not exist.)
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// CompInputs — NAME the inputs behind a computed row (Alex, 2026-07-19).
//
// A fabricated (MF) line has NO unit price of its own (`catalog_lines.price` is
// NULL) and its hours come from `output_rate`, so the grid showed only the RESULTS
// — Material / Hours / Labor — with "Unit $" and "$/Hr" blank. That reads as
// broken rather than as computed. Alex: show the Material & Labour labels in the
// component list. Naming both inputs is unambiguous where "Unit $" is not —
// per kg of WHICH material?
//
//   fabricated : <material> @ <rm price>/<unit>  ·  <fab trade> @ <rate>/hr
//   install    : <the product's labour line> @ <rate>/hr
//   HR row     : nothing — the row IS the labour, its own price is the rate
//
// Renders into the slot the Component cell already reserves (it held a bare
// "—"), so no column is added and no layout shifts.
// ---------------------------------------------------------------------------
function CompInputs({ row, laborInfo }) {
  const cls = row._l1_class;
  const bits = [];

  if (cls === 'MF') {
    if (row.line_material) {
      bits.push(row.rm_price != null
        ? `${row.line_material} @ ${rate(row.rm_price, row.rm_unit || '')}`
        : `${row.line_material} — no priced stock row`);
    }
    // Prefer the HR line's label — fab_trade is dormant since 2026-07-19 and only
    // appears if a line hasn't been pointed at a labour line yet.
    const fabLabour = row.fab_labor_label || row.fab_trade;
    if (fabLabour) {
      bits.push(row.fab_rate_per_hour != null
        ? `${fabLabour} @ ${rate(row.fab_rate_per_hour, 'hr')}`
        : `${fabLabour} — no labour line set, hours not charged`);
    }
  } else if (cls !== 'HR' && Number(row.line_hrs) > 0) {
    bits.push(laborInfo?.label
      ? `${laborInfo.label} @ ${laborInfo.rate != null ? rate(laborInfo.rate, 'hr') : '—/hr'}`
      : 'no labour set — hours not charged');
  }

  if (!bits.length) return DASH_CELL;
  return (
    <span
      style={{
        fontSize: 'var(--fs-micro)', color: 'var(--th-text-secondary)',
        display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}
      title={bits.join('  ·  ')}
    >
      {bits.join('  ·  ')}
    </span>
  );
}

export function makeGridCols(mode, onNavigateToCatalog, onNavigateToProduct, laborInfo = null) {
  const $ = v => v != null ? <MoneyCell n={v} bold /> : <Text kind="empty" />;
  const dash = <Text kind="empty" />;
  return [
    {
      // F-uismalls: ↗ nav icon MOVED here — immediately before the # column
      // (after the drag/edit icon-column group), one consistent position for
      // both kinds instead of sitting next to Ref. Resolution logic unchanged (CO1):
      // kind='line' (sized/fam:) — resolve l1/l2/l3 from the attached sized_lines
      // (or the family's l1 alone as a fallback) so a family ref still links.
      // kind='line' (plain)     — cl.l1/l2/l3 from the catalog-line join; no l3 requirement,
      //                            navigates to the deepest level present.
      // kind='product'          — ref_id is the sub-product id; opens it in Products.
      key: '_ref_nav', label: '', width: 26, align: 'center',
      render: row => {
        let onNav = null;
        if (row.kind === 'product') {
          if (onNavigateToProduct && row.ref_id) {
            onNav = (e) => { e.stopPropagation(); onNavigateToProduct(row.ref_id); };
          }
        } else {
          const sizedLine = row.size_mode === 'sized' ? (row.sized_lines || [])[0] : null;
          const l1 = sizedLine?.l1 || row._family_l1 || row.l1;
          const l2 = sizedLine?.l2 || row.l2;
          const l3 = sizedLine?.l3 || row.l3;
          if (onNavigateToCatalog && l1) {
            onNav = (e) => { e.stopPropagation(); onNavigateToCatalog(l1, l2, l3); };
          }
        }
        if (!onNav) return null;
        return (
          <button
            onClick={onNav}
            title="Open reference"
            style={{ ...BTN_GLYPH, lineHeight: 1 }}
          >↗</button>
        );
      },
    },
    { key: 'description', label: 'Description', width: 150, editable: true },
    {
      // S3h — Source + Ref merged column. Read: the Source icon (▣ Catalog / ⊞ Product —
      // the word lives in the title) + the mono ref code. Edit: the editRender
      // (ProductsView) renders the Source dropdown + the L1▸L2▸L3 cascade INLINE on one
      // row (S3h, was stacked in S3g) — widened so all 4 short controls (icon + 3 short
      // codes, e.g. RM/2/R2B) fit on that one line. A5: still plain text in read mode —
      // navigation is the ↗ icon column above, never the code.
      key: 'ref_id', label: 'Source · Ref', width: 230,
      render: row => {
        const m = KIND_META[row.kind] || { icon: '?', label: row.kind, color: 'var(--th-text-hint)' };
        // A SYNCED component shows the Onshape cube instead of the kind glyph
        // (Alex, 2026-07-31: "that logo should be the 3D cube because Source is
        // Onshape"). On a HYBRID product — the normal case, since a hybrid can
        // only be assembled here and not in Onshape — this is the only thing on
        // the row that says where the component came from, and therefore which
        // ones a re-sync owns. The kind is not lost: it stays in the title.
        // Shape, not colour: OnshapeMark is an isometric cube (colour-blind safe,
        // the rule OnshapeLink already follows).
        const fromOnshape = row.source === 'onshape';
        // THM-5 — error beats locked here too, matching ProductsView's getRowStyle
        // priority. The row backgrounds (soft red vs soft grey) read too close for
        // a colour-blind scan to tell apart on their own (RULES rule 3); this icon,
        // in the same spot either way, is the shape that actually does the telling.
        return (
          <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5, fontSize: 'var(--fs-small)' }}
                title={row.formula_error ? `Formula error: ${row.formula_error}` : (fromOnshape ? `Onshape · ${m.label}` : m.label)}>
            {row.formula_error
              ? <span aria-hidden="true" style={{ color: 'var(--th-status-deprecated)', fontWeight: 700 }}>{WARN}</span>
              : fromOnshape
                ? <OnshapeMark size={12} aria-hidden="true" />
                : <span aria-hidden="true" style={{ color: m.color, fontWeight: 700 }}>{m.icon}</span>}
            <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)', color: 'var(--th-text-secondary)' }}>
              {row.ref_display || row.ref_id || '—'}
            </span>
          </span>
        );
      },
    },
    {
      // F-uismalls: the "sub-assembly" chip was dropped — redundant with the
      // Source column (⊞ Product). row._is_sub_assembly itself is untouched
      // (still used for costing/markers elsewhere), only this visual chip goes.
      // Q8 v2 (Mockup B): a param_map comp additionally renders its mapping
      // feedback (chip + optional linked-pairs line) inline, under the label —
      // a slim sub-row, not a separate expander. S4: a catalog-line comp (no
      // config possible) shows a plain "—" in the same slot instead.
      key: 'label', label: 'Label', width: 150,
      render: row => (
        <div>
          <div>{row.label}</div>
          {row.kind === 'product'
            ? <MappingFeedback row={row} />
            : <CompInputs row={row} laborInfo={laborInfo} />}
        </div>
      ),
    },
    {
      key: 'size_display', label: 'Size', width: 130,
      render: row => {
        if (row.formula_error) {
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 'var(--fs-small)', color: 'var(--th-status-deprecated)' }}>
              <span>⚠</span>
              <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-micro)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 90 }}
                    title={row.formula_error}>
                {row.formula_error}
              </span>
            </span>
          );
        }
        const mode_ = row.size_mode || 'NA';
        // A product comp bound via param_map has size_mode='NA' but the resolver still
        // gives it a real resolved look + marker (product-resolver.js _size_display) —
        // render it like a sized line. Only show the plain NA badge when there is
        // genuinely no resolved size (size_display absent or itself 'NA').
        if (mode_ === 'NA' && (row.size_display == null || row.size_display === 'NA')) {
          return <Text kind="empty">NA</Text>;
        }
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-small)', maxWidth: '100%', overflow: 'hidden' }}>
            <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)', color: 'var(--th-text-body)', flexShrink: 0 }}>
              {row.size_display || '—'}
            </span>
            {row.size_marker && (
              <span style={{ color: 'var(--th-text-disabled)', fontSize: 'var(--fs-micro)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                ↳{row.size_marker}
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: 'effective_qty', label: mode === 'placement' ? 'Eff. Qty' : 'Qty', width: 62, align: 'right', textKind: 'small',
      // CO11: show a color-blind-safe FORMULA badge when the qty is a formula (qty_expr present
      // and not a bare numeric constant). Display-only — no editor change, no evaluation here.
      // A3: the badge pinned at the LEFT edge of the cell (MoneyCell's $-left pattern), number
      // right-aligned in the remaining space. A2: qty formatted 0-decimals + thousand
      // separators; exact value kept in the title tooltip when rounding hides precision.
      render: row => {
        const q = row.effective_qty;
        const rounded = q != null ? formatQty(q) : null;
        const hidesPrecision = q != null && Number.isFinite(Number(q)) && Math.round(Number(q)) !== Number(q);
        return (
          <span style={{ display: 'flex', width: '100%', alignItems: 'baseline' }}>
            <span style={{ flexShrink: 0, width: 12 }}>
              {isFormulaQty(row.qty_expr) && (
                <span aria-hidden="true" title={`formula: ${row.qty_expr}`}
                      style={{ fontFamily: 'var(--ff-mono)', fontStyle: 'italic', fontWeight: 700, fontSize: 'var(--fs-small)', color: 'var(--th-navy)', lineHeight: 1 }}>
                  {FORMULA}
                </span>
              )}
            </span>
            <span
              title={hidesPrecision ? String(q) : undefined}
              style={q != null
                ? { flex: 1, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 'var(--fs-small)' }
                : { flex: 1, textAlign: 'right', color: 'var(--th-text-faint)' }}
            >
              {rounded ?? '—'}
            </span>
          </span>
        );
      },
    },
    {
      key: 'line_unit', label: 'Unit', width: 38,
      // A QUANTITY-BASED sub-product has no line_unit — this column reads the catalog
      // line, and a sub-product is not one. Its Qty is a size, so the unit that gives
      // it meaning is the DRIVER parameter's (Alex, 2026-08-03: "Total is good now,
      // but no unit" — the number said 10,320 and nothing said "kg").
      render: row => <span style={{ fontSize: 'var(--fs-small)', color: 'var(--th-text-disabled)' }}>{row.line_unit || row._sub_qty_unit || '—'}</span>,
    },
    {
      key: 'line_price', label: 'Unit Cost', width: 75, align: 'right',
      render: row => row.line_price != null ? <MoneyCell n={row.line_price} unit /> : dash,
    },
    {
      key: 'mat_cost', label: 'Supply', width: 75, align: 'right',
      render: row => $(row.mat_cost),
    },
    {
      key: 'hours', label: 'Hours', width: 58, align: 'right',
      // Hours nothing charges (no labour line resolves) show as ⚠ + the hours, in
      // the warning colour, instead of a dash that reads as "no hours" — the
      // Components card's notice says why, in words.
      render: row => row.hours != null ? <HourCell n={row.hours} />
        : row.uncharged_hours > 0 ? (
          <span title={`${formatQty(row.uncharged_hours)} h not charged: this product has no labour line`}
                style={{ whiteSpace: 'nowrap', color: 'var(--th-status-draft)' }}>
            {WARN} <HourCell n={row.uncharged_hours} color="var(--th-status-draft)" />
          </span>
        ) : dash,
    },
    {
      key: 'labor_rate', label: '$/Hr', width: 50, align: 'right',
      // Was a bare unformatted number ("42", no $/decimals) — the exact
      // hand-rolled-money gap this centralisation exists to close.
      render: row => row.labor_rate != null
        ? <Text kind="note" style={{ fontVariantNumeric: 'tabular-nums' }}>{rate(row.labor_rate, 'hr')}</Text>
        : dash,
    },
    {
      key: 'labor_cost', label: 'Labor', width: 75, align: 'right',
      render: row => $(row.labor_cost),
    },
    {
      key: 'line_cost', label: 'Cost', width: 75, align: 'right',
      render: row => $(row.line_cost),
    },
  ];
}

// componentsCountLabel(rowCount, notSyncedCount) — the COMPONENTS card's count,
// for every surface that shows a components grid.
//
// Alex, 2026-08-21: "same must be the same" — and then, sharper: "is it the same
// card, or a copy?" This existed as the SAME SENTENCE WRITTEN TWICE, once in
// ProductsView's `components` spot and once in OnshapePanel's, because that was
// the quickest way to make the two screens agree on the day. Two copies agree
// exactly once — on the day they are written — and the whole point of the rule is
// that they keep agreeing. So the sentence lives here, next to the columns both
// grids already share, and neither screen owns a private version of it.
export function componentsCountLabel(rowCount, notSyncedCount = 0) {
  const n = Number(rowCount) || 0;
  return `${n} item${n !== 1 ? 's' : ''}${notSyncedCount ? ` · ${notSyncedCount} not synced` : ''}`;
}
